import { useCallback, useEffect, useMemo, useState } from 'react';

import { ConfirmationCode, LiveCardProps, LiveStatusEnum, useLives } from 'features/lives';

import { Flex, LiveCard, Modal, Typography, ButtonIcon, Button, Loading } from 'components';

import { AlertState } from 'features/alert/types';
import { LiveMeetingAttendanceConfirmationStatusEnum, LiveMeetingAttendanceConfirmationTypeEnum } from 'app/types/cards/LiveVideoCard';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

interface EventconfirmationProps {
  openEventConfirmation: boolean;
  checkinLiveMeetingUuid: string;
  confirmationStatus: LiveMeetingAttendanceConfirmationStatusEnum;
  confirmationCodeExpiresAt?: string;
  onclose: (value: boolean) => void;
}

const EventConfirmation = ({
  openEventConfirmation,
  checkinLiveMeetingUuid,
  confirmationStatus,
  onclose,
  confirmationCodeExpiresAt,
}: EventconfirmationProps) => {
  const { confirmAttendance } = useLives();

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertState>(undefined!);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  const handleClose = () => {
    setConfirmationCode('');
    setAlert(undefined!);
    onclose(false);
  };

  const validConfirmationCode = useMemo(() => confirmationCode?.trim().length === 6, [confirmationCode]);

  const handleEventConfirmation = useCallback(
    async (setCallback = true) => {
      if (confirmationCode) {
        setLoading(true);

        await confirmAttendance(
          checkinLiveMeetingUuid,
          confirmationCode,
          validConfirmationCode
            ? LiveMeetingAttendanceConfirmationTypeEnum.InputConfirmationCode
            : LiveMeetingAttendanceConfirmationTypeEnum.Unspecified
        )
          .then(async () => {
            if (setCallback) {
              //setar  confirmationStatus: LiveMeetingAttendanceConfirmationStatusEnum.Confirmed
            }
          })
          .catch((error: { apiResultCode: number; apiResultMessage: string }) => {
            setAlert({ type: 'error', message: error?.apiResultMessage || 'Ocorreu um erro ao confirmar presença' });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setAlert({ type: 'error', message: 'Informe o código para confirmar sua presença' });
      }
    },
    [validConfirmationCode, confirmationCode]
  );

  useEffect(() => {
    if (confirmationCode?.length === 0 || confirmationCode?.length === 6) setAlert(undefined!);
  }, [confirmationCode]);

  useEffect(() => {
    return handleClose;
  }, []);

  return openEventConfirmation ? (
    <Modal
      bg="transparent"
      onClose={handleClose}
      buttonClose={
        <ButtonIcon
          type="button"
          icon="close"
          width={30}
          height={30}
          bg="transparent"
          color="primary_200"
          colorHover="white"
          borderRadius={100}
          iconWidth={22}
          mr={-100}
        />
      }
    >
      {loading && <Loading alpha />}

      <Flex flexDirection="column" flexWrap="nowrap">
        {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Confirmed && (
          <Flex
            bg="white"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={4}
            borderBottomLeftRadius={4}
            borderBottomRightRadius={4}
          >
            <ButtonIcon icon="accountCheck" bg="transparent" color="success_400" cursor="inherit" iconWidth={32} />

            <Typography mt={3} fontWeight="bold">
              Sua presença foi confirmada
            </Typography>

            {/* {!!confirmationCodeExpiresAt && (
              <Typography mt={1} type="type0" color="black50">
                em {formatDateFromUtcToTimezone(confirmationCodeExpiresAt).format('DD/MM/YYYY hh:mm')}
              </Typography>
            )} */}
          </Flex>
        )}

        {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Expired && (
          <Flex
            bg="white"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={4}
            borderBottomLeftRadius={4}
            borderBottomRightRadius={4}
          >
            <ButtonIcon icon="accountCancel" bg="transparent" color="alert_400" cursor="inherit" iconWidth={32} />

            <Typography mt={3} fontWeight="bold">
              Confirmação de presença expirada
            </Typography>

            {!!confirmationCodeExpiresAt && (
              <Typography mt={1} type="type0" color="black50">
                em {formatDateFromUtcToTimezone(confirmationCodeExpiresAt).format('DD/MM/YYYY hh:mm')}
              </Typography>
            )}
          </Flex>
        )}

        {confirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Pending && (
          <Flex position="relative">
            <Flex
              flexDirection="column"
              justifyItems="flex-start"
              width={320}
              bg="white"
              p={3}
              borderBottomLeftRadius={4}
              borderBottomRightRadius={4}
              position="relative"
              zIndex={4}
              mt={'0'}
              opacity={1}
              style={{
                transitionDelay: '0s',
                transitionProperty: 'margin,opacity',
                transitionTimingFunction: 'ease-in-out',
                transitionDuration: '0.5s',
              }}
            >
              <Typography type="type1" fontWeight="bold" textAlign="center" mb={3}>
                Informe o código apresentado durante o evento ao-vivo:
              </Typography>

              <ConfirmationCode hasError={!!alert?.message} confirmationCode={confirmationCode} setConfirmationCode={setConfirmationCode} />

              {alert?.type === 'error' && (
                <Typography type="type1" color="alert_400" textAlign="center" mt={2}>
                  {alert?.message}
                </Typography>
              )}
              {alert?.type === 'success' && (
                <Typography type="type1" color="success_400" textAlign="center" mt={2}>
                  {alert?.message}
                </Typography>
              )}

              <Button
                mt={3}
                flex={1}
                bg={!validConfirmationCode ? 'gray_500' : 'primary_400'}
                bgHover={!validConfirmationCode ? 'gray_500' : 'primary_200'}
                color={!validConfirmationCode ? 'black80' : 'white'}
                colorHover={!validConfirmationCode ? 'black80' : 'white'}
                disabled={!validConfirmationCode}
                flexShrink={0}
                p={3}
                onClick={handleEventConfirmation}
              >
                Confirmar presença
              </Button>
            </Flex>

            {/* <Button
              borderTopLeftRadius={0}
              borderTopRightRadius={0}
              width="100%"
              position="absolute"
              zIndex={3}
              bg="gray_600"
              bgHover="primary_400"
              p={4}
              onClick={() => setShowConfirmationInput(true)}
            >
              Confirmar presença com código
            </Button> */}
          </Flex>
        )}
      </Flex>
    </Modal>
  ) : (
    <></>
  );
};

export default EventConfirmation;
